import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, stageCenterNdc, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { artProxyUrl } from '../artwork-texture.js';
import { Adapt } from './lib/adapt.js';
import { vdjBeatBreath, vdjColor, VDJ_PRESETS } from './lib/vdjLook.js';

export const PROFILE = {
  id: 'bokeh',
  family: 'particles',
  danceability: 0.46,
  requiresTier: 0,
  energyLevel: 0.30,
  bpmMin: 60,
  bpmMax: 120,
  movement: 0.30,
  tonality: 0.85,
  enabled: true
};

// BOKEH : une ville la nuit vue à travers un objectif très ouvert. Des
// centaines de sources ponctuelles en 3D, chacune rendue comme le disque que
// fait un vrai objectif : son rayon en pixels est le CERCLE DE CONFUSION,
// proportionnel à |1 - F / z| (F le plan de mise au point, z la profondeur de
// la source). Sur la phrase, la mise au point bascule : les disques se
// resserrent en points nets et révèlent une scène - une avenue, la pochette en
// mosaïque de lumières, le titre écrit en points, un ciel d'étoiles et de
// lampions - puis repassent dans le flou, où les sources glissent vers la
// scène suivante.
//
// L'OPTIQUE. La mise au point est interpolée en DIOPTRIES (1/F), pas en
// distance : le flou varie alors régulièrement. L'ouverture se ferme en même
// temps que la scène vient au point - c'est elle qui rend la profondeur de
// champ lisible - et les disques prennent la forme de l'ouverture à 7 lames
// (heptagone arrondi). En bord d'image, l'intersection avec un second cercle
// décalé radialement donne l'oeil de chat (vignettage mécanique). Bord du
// disque un peu plus lumineux, comme les vrais bokehs.
//
// LE RENDU (R1, R2, R12). Un quad instancié par source, taille en pixels
// calculée dans le vertex shader, bornée (recouvrement limité). Les grands
// disques (rayon > 4 px) vont dans une cible en DEMI-RÉSOLUTION, mélange
// additif en HalfFloat, puis la composition les écrête en douceur en
// préservant la teinte : aucune zone chargée ne sature en blanc. Les points
// nets (rayon < 4 px) sont tracés en pleine résolution par-dessus, avec leur
// petit halo. Les deux couches se relaient par un fondu sur le rayon.
// Luminance d'un disque : flux x (rRef / r)^1.25 - un grand disque est plus
// pâle, un point net n'éblouit jamais.
//
// LE TEMPS (R20). Net vers 4 s (la pochette) et vers 14 s (le titre), puis à
// chaque phrase (ou toutes les 14 s sans grille) : avenue, ciel, pochette,
// titre... La pochette passe en lumières une fois par cycle de quatre scènes.
// Net pendant 2 mesures, entrée 1,6 s et sortie 1,8 s en smootherstep.
//
// LA MUSIQUE (adapt.js, lounge) : temps -> respiration de la mise au point
// (3 % en dioptries) ; mesure -> une vague de scintillement traverse l'écran ;
// phrase -> scène suivante, au point ; drop -> flou maximal en 0,35 s, puis les
// sources se resserrent sur la pochette.
//
// La pochette en mosaïque se place à GAUCHE du bloc pochette et le titre à
// DROITE (R6, R19 : autour du centre de composition) : au centre, la pochette
// DOM la cacherait.

const TIERS = { 0: { n: 300, blades: false, rMax: 0.055, rows: 9 }, 1: { n: 600, blades: true, rMax: 0.068, rows: 11 }, 2: { n: 900, blades: true, rMax: 0.08, rows: 11 } };
const Z_FLAT = 10;              // the plane of the mosaic and the title
const INV_F_BLUR = 1 / 1.2;     // focused nearer than everything: all in blur
const AP_BLUR = 0.044;          // the blur circle scale, in screen heights
const AP_BURST = 0.085;         // the drop's burst
const R_REF = 0.0085;           // the disc size that keeps its full flux (screen heights)
const FLUX_GAIN = 0.7;
const T_IN = 1.6, T_OUT = 1.8, T_EVENTS = [4, 14], EVENT_GAP = 14;
const SCENES = ['cover', 'title', 'avenue', 'sky'];
const SCENE_OPTICS = { cover: { z: Z_FLAT, ap: 0.25 }, title: { z: Z_FLAT, ap: 0.25 }, avenue: { z: 10, ap: 0.17 }, sky: { z: 26, ap: 0.11 } };
const BREATH = VDJ_PRESETS.lounge;
const WARM = [[1.0, 0.62, 0.22], [1.0, 0.86, 0.62], [1.0, 0.25, 0.18], [1.0, 0.36, 0.62]];
const COOL = [[0.25, 0.85, 1.0], [0.75, 0.88, 1.0]];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smoother(x) { x = clamp01(x); return x * x * x * (x * (x * 6 - 15) + 10); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function lin(c) { return c.map((v) => Math.pow(v, 2.2)); }
function rng(seed) { let s = seed >>> 0 || 1; return () => { s = (s * 16807) % 2147483647; return s / 2147483647; }; }

// --- the cover, sampled in a worker (R17) -----------------------------------------------
function gridWorkerSource() {
  return 'self.onmessage = function (ev) {\n' +
    '  var m = ev.data, t0 = performance.now();\n' +
    '  var fail = function (e) { self.postMessage({ id: m.id, error: String(e && e.message || e) }); };\n' +
    '  if (typeof OffscreenCanvas === "undefined" || typeof createImageBitmap === "undefined") { fail("nooffscreen"); return; }\n' +
    '  fetch(m.url).then(function (r) { if (!r.ok) throw new Error("http " + r.status); return r.blob(); })\n' +
    '    .then(function (b) { return createImageBitmap(b); })\n' +
    '    .then(function (bmp) {\n' +
    '      var c = new OffscreenCanvas(m.g, m.g), g = c.getContext("2d");\n' +
    '      g.imageSmoothingQuality = "high";\n' +
    '      var s = Math.min(bmp.width, bmp.height);\n' +
    '      g.drawImage(bmp, (bmp.width - s) / 2, (bmp.height - s) / 2, s, s, 0, 0, m.g, m.g);\n' +
    '      if (bmp.close) bmp.close();\n' +
    '      var d = new Uint8Array(g.getImageData(0, 0, m.g, m.g).data);\n' +
    '      self.postMessage({ id: m.id, data: d, ms: performance.now() - t0 }, [d.buffer]);\n' +
    '    }).catch(fail);\n' +
    '};\n';
}
let _worker = null, _workerFailed = false, _nextId = 0;
const _waiting = new Map();
function askWorker(msg, cb) {
  if (!_worker && !_workerFailed) {
    try {
      if (typeof Worker === 'undefined') throw new Error('no worker');
      _worker = new Worker(URL.createObjectURL(new Blob([gridWorkerSource()], { type: 'text/javascript' })));
      _worker.onmessage = (ev) => { const f = _waiting.get(ev.data.id); if (f) { _waiting.delete(ev.data.id); f(ev.data); } };
      _worker.onerror = () => { for (const [id, f] of _waiting) f({ id, error: 'worker' }); _waiting.clear(); };
    } catch (e) { _workerFailed = true; _worker = null; }
  }
  if (!_worker) return false;
  _waiting.set(msg.id, cb);
  _worker.postMessage(msg);
  return true;
}
// no cover: a radial gradient of the palette's two first colours
function fallbackGrid(palette, g) {
  const src = palette && (palette.vividColors || palette.dominantColors) || [];
  const a = vdjColor(src[0] != null ? src[0] : 0xff7a3c, new THREE.Color()), b = vdjColor(src[1] != null ? src[1] : 0x3cc8ff, new THREE.Color());
  const out = new Uint8Array(g * g * 4), c = new THREE.Color();
  for (let y = 0; y < g; y++) for (let x = 0; x < g; x++) {
    const r = Math.hypot(x - (g - 1) / 2, y - (g - 1) / 2) / (g / 2);
    c.copy(a).lerp(b, clamp01(r)).convertLinearToSRGB();
    const o = (y * g + x) * 4;
    out[o] = c.r * 255; out[o + 1] = c.g * 255; out[o + 2] = c.b * 255; out[o + 3] = 255;
  }
  return out;
}

// --- the title in dots: drawn once on a canvas, 8x supersampled, cell coverage --------------
function shortTitle(t) {
  t = String(t || '').replace(/\s*[([].*?[)\]]\s*/g, ' ').replace(/\s+-\s+.*$/, '').trim();
  if (t.length <= 26) return t;
  const words = t.split(/\s+/); let out = '';
  for (const w of words) { if ((out + ' ' + w).trim().length > 26) break; out = (out + ' ' + w).trim(); }
  return out || t.slice(0, 26);
}
// returns { cells: [[cx, cy]...] in cell units, cols, rows } or null
export function titleDots(text, rowsPerLine, maxLines, maxCols) {
  if (typeof document === 'undefined' || !text) return null;
  const SS = 8, fontPx = rowsPerLine * SS * 1.08;
  const c = document.createElement('canvas'), g = c.getContext('2d');
  const font = `700 ${fontPx}px "Helvetica Neue", Arial, sans-serif`;
  g.font = font;
  const words = text.split(/\s+/).filter(Boolean);
  const cellW = (s) => Math.ceil(g.measureText(s).width / SS) + 1;
  // balanced wrapping into 1..maxLines lines, the fewest lines that fit maxCols
  let lines = null;
  for (let L = 1; L <= maxLines && !lines; L++) {
    const target = cellW(text) / L;
    const ls = []; let cur = '';
    for (const w of words) { const t = (cur + ' ' + w).trim(); if (cur && cellW(t) > target * 1.15 && ls.length < L - 1) { ls.push(cur); cur = w; } else cur = t; }
    ls.push(cur);
    if (Math.max(...ls.map(cellW)) <= maxCols) lines = ls;
  }
  if (!lines) { lines = [text]; while (lines[0].length > 3 && cellW(lines[0]) > maxCols) lines[0] = lines[0].slice(0, -1); }
  const gap = Math.max(2, Math.round(rowsPerLine * 0.3));
  const cols = Math.max(...lines.map(cellW)), rows = lines.length * rowsPerLine + (lines.length - 1) * gap;
  c.width = cols * SS; c.height = rows * SS;
  g.font = font; g.fillStyle = '#fff'; g.textBaseline = 'alphabetic'; g.textAlign = 'center';
  lines.forEach((l, i) => g.fillText(l, c.width / 2, (i * (rowsPerLine + gap) + rowsPerLine * 0.86) * SS));
  const img = g.getImageData(0, 0, c.width, c.height).data, cells = [];
  for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) {
    let s = 0;
    for (let yy = 0; yy < SS; yy++) for (let xx = 0; xx < SS; xx++) s += img[(((y * SS + yy) * c.width) + x * SS + xx) * 4 + 3];
    if (s / (SS * SS * 255) > 0.38) cells.push([x, y]);
  }
  return { cells, cols, rows };
}

// --- shaders --------------------------------------------------------------------------------
const discVertex = `
  precision highp float;
  attribute vec4 aFrom;          // x, y, z (view space; screen = xy / z), flux
  attribute vec4 aTo;
  attribute vec4 aColFrom;       // linear rgb, sharp radius (screen heights)
  attribute vec4 aColTo;
  attribute vec4 aSeed;
  attribute vec4 aScatter;       // where the source floats while out of focus: screen x, y (0..1), depth, -
  uniform float uConv;           // 0 scattered (blur) .. 1 on the scene (sharp)
  uniform vec2 uStageNdc;
  uniform float uPx;             // screen heights per FULL-resolution pixel
  uniform float uLayerScale;     // 0.5 for the half-resolution discs, 1 for the points
  uniform float uLayer;          // 0 discs, 1 points
  uniform float uG;              // glide from -> to
  uniform float uInvF;           // focus, in dioptres
  uniform float uAp;             // blur circle scale (screen heights)
  uniform float uRMax;           // bounded disc radius (screen heights)
  uniform float uDrift;
  uniform float uTime;
  uniform vec3 uWave;            // front (screen x), amount, width
  uniform float uRRef;
  uniform float uGain;
  ${SAFE_ZONE_VERTEX_CHUNK}
  varying vec2 vP;               // px from the centre (this layer's pixels)
  varying float vR;              // radius, this layer's pixels
  varying vec3 vCol;
  varying vec2 vCat;
  void main() {
    vec4 A = mix(aFrom, aTo, uG);
    vec4 C = mix(aColFrom, aColTo, uG);
    float flux = A.w;
    if (flux < 0.003) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float ph = aSeed.x * 6.2832, fr = 0.05 + 0.09 * aSeed.y;
    // out of focus the sources float over the whole frame, each at its own
    // depth; they close in on the scene as the focus comes
    vec2 sS = (aScatter.xy - 0.5) * vec2(uAspect * 1.04, 1.04);
    sS += vec2(sin(uTime * fr * 0.6 + ph * 2.3), cos(uTime * fr * 0.5 + ph * 1.3)) * 0.05;
    float z = mix(aScatter.z, A.z, uConv);
    vec2 s = mix(sS, A.xy / A.z, uConv);
    s += uDrift * vec2(sin(uTime * fr + ph), cos(uTime * fr * 0.83 + ph * 1.7)) * (0.004 + 0.008 * aSeed.z);
    // the circle of confusion: |1 - F / z|
    float coc = uAp * abs(1.0 - 1.0 / (uInvF * z));
    // a negative sharp radius marks a filler: it fades as the scene comes into focus
    float filler = step(C.w, 0.0);
    flux *= 1.0 - 0.97 * filler * uConv;
    // out of focus, a photograph's spread: a few bright discs, many pale ones
    flux *= mix(0.18 + 0.82 * aScatter.w * aScatter.w, 1.0, uConv);
    float r = clamp(max(coc, abs(C.w)), 0.9 * uPx, uRMax);
    float rPx = r / uPx;
    float wBig = smoothstep(3.0, 5.0, rPx);
    float w = uLayer < 0.5 ? wBig : 1.0 - wBig;
    if (w < 0.002) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float peak = flux * pow(min(1.0, uRRef / r), 1.25) * uGain;
    // the bar's wave of sparkle, a gentle twinkle of their own
    float wv = exp(-pow((s.x - uWave.x) / uWave.z, 2.0));
    peak *= 1.0 + uWave.y * wv * (0.6 + 0.4 * sin(uTime * 31.0 + aSeed.w * 50.0));
    peak *= 1.0 + 0.10 * aSeed.z * sin(uTime * (1.1 + 2.0 * aSeed.y) + ph);
    float rl = rPx * uLayerScale;
    float hsz = rl + 1.2 + (uLayer > 0.5 ? 7.0 : 0.0);
    vec2 corner = position.xy * hsz;
    vec2 h = s + corner * uPx / uLayerScale;
    gl_Position = vec4(h.x * 2.0 / uAspect + uStageNdc.x, h.y * 2.0 + uStageNdc.y, 0.0, 1.0);
    vec4 centre = vec4(s.x * 2.0 / uAspect + uStageNdc.x, s.y * 2.0 + uStageNdc.y, 0.0, 1.0);
    float safe = safeZoneAttenuation(centre);
    vP = corner; vR = rl;
    vCol = C.rgb * peak * w * safe;
    // cat's eye: the disc cut by a second circle shifted along the radius
    float e = length(s) / (0.5 * length(vec2(uAspect, 1.0)));
    vCat = (length(s) > 1e-4 ? normalize(s) : vec2(0.0)) * 0.62 * e * e;
  }
`;
const discFragment = `
  precision highp float;
  uniform float uBlade;
  varying vec2 vP; varying float vR; varying vec3 vCol; varying vec2 vCat;
  void main() {
    vec2 q = vP / max(vR, 0.5);
    float lq = length(q);
    // the 7-blade aperture, rounded
    float sector = 0.8976;                       // 2 pi / 7
    float a = atan(q.y, q.x) + 0.3;
    float ang = mod(a + sector * 0.5, sector) - sector * 0.5;
    float hept = lq * cos(ang) / 0.90097;        // cos(pi / 7): 1 on the edges
    float d1 = mix(lq, mix(hept, lq, 0.25), uBlade);
    float d = max(d1, length(q - vCat));
    float aa = 1.0 / max(vR, 0.5);
    float cov = 1.0 - smoothstep(1.0 - aa, 1.0 + aa, d);
    if (cov <= 0.0) discard;
    float ring = 0.80 + 0.42 * smoothstep(0.70, 0.97, d);
    gl_FragColor = vec4(vCol * cov * ring, 1.0);
  }
`;
const pointFragment = `
  precision highp float;
  varying vec2 vP; varying float vR; varying vec3 vCol; varying vec2 vCat;
  void main() {
    float d = length(vP);
    float core = 1.0 - smoothstep(vR - 0.6, vR + 0.6, d);
    float glow = 0.05 * exp(-max(d - vR, 0.0) / 3.0) * (1.0 - smoothstep(5.0, 7.0, d - vR));
    vec3 c = vCol * (core * (0.9 + 0.1 * (1.0 - d / max(vR, 0.6))) + glow);
    if (max(c.r, max(c.g, c.b)) < 0.0005) discard;
    gl_FragColor = vec4(c, 1.0);
  }
`;
const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// the discs back at full size, compressed above 0.6 keeping their hue: never white
const compositeFragment = `
  precision highp float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uTex, vUv).rgb;
    float m = max(c.r, max(c.g, c.b));
    float k = m > 0.6 ? (0.6 + 0.34 * (1.0 - exp(-(m - 0.6) / 0.34))) / m : 1.0;
    gl_FragColor = vec4(c * k, 1.0);
  }
`;

const _bs = new THREE.Vector2();

export class BokehVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._L = TIERS[this._tier] || TIERS[1];
    const sc = urlParam('bokehscene');
    this._forceScene = SCENES.includes(sc) ? sc : null;
    const f = parseFloat(urlParam('bokehfocus'));
    this._forceFocus = Number.isFinite(f) ? clamp01(f) : null;
  }

  // lounge: a light bloom on the sharp points only, no split, no flash
  get postProcessing() { return { bloomStrength: 0.16, bloomThreshold: 0.86, aberration: 0, grain: 0.01, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }; }

  init(scene) {
    this._scene = scene;
    this._adapt = new Adapt();
    const N = this._L.n;
    this._N = N;
    this._G = Math.floor(Math.sqrt(N));
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    this._arr = { from: new Float32Array(N * 4), to: new Float32Array(N * 4), cf: new Float32Array(N * 4), ct: new Float32Array(N * 4) };
    const seed = new Float32Array(N * 4), R = rng(91);
    for (let i = 0; i < N * 4; i++) seed[i] = R();
    this._attr = {
      from: new THREE.InstancedBufferAttribute(this._arr.from, 4), to: new THREE.InstancedBufferAttribute(this._arr.to, 4),
      cf: new THREE.InstancedBufferAttribute(this._arr.cf, 4), ct: new THREE.InstancedBufferAttribute(this._arr.ct, 4)
    };
    for (const k in this._attr) this._attr[k].setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aFrom', this._attr.from); geo.setAttribute('aTo', this._attr.to);
    geo.setAttribute('aColFrom', this._attr.cf); geo.setAttribute('aColTo', this._attr.ct);
    geo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 4));
    const sc = new Float32Array(N * 4), R2 = rng(173);
    for (let i = 0; i < N; i++) {
      sc[i * 4] = R2(); sc[i * 4 + 1] = R2();
      // depths: most at mid distance, a few close (smaller discs: |1 - F / z| shrinks near F)
      sc[i * 4 + 2] = R2() < 0.15 ? 1.7 + R2() * 1.3 : 4 + Math.pow(R2(), 1.5) * 50; sc[i * 4 + 3] = R2();
    }
    geo.setAttribute('aScatter', new THREE.InstancedBufferAttribute(sc, 4));
    geo.instanceCount = N;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this._geo = geo;
    this._u = {
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather,
      uStageNdc: { value: new THREE.Vector2() }, uPx: { value: 1 / 720 }, uG: { value: 1 }, uInvF: { value: INV_F_BLUR }, uAp: { value: AP_BLUR },
      uRMax: { value: this._L.rMax }, uDrift: { value: 1 }, uTime: { value: 0 }, uWave: { value: new THREE.Vector3(-9, 0, 0.12) }, uRRef: { value: R_REF }, uGain: { value: FLUX_GAIN },
      uBlade: { value: 0 }, uConv: { value: 0 }
    };
    const mat = (frag, layer, scale) => new THREE.ShaderMaterial({
      uniforms: Object.assign({}, this._u, { uLayer: { value: layer }, uLayerScale: { value: scale } }),
      vertexShader: discVertex, fragmentShader: frag, depthTest: false, depthWrite: false, transparent: true, side: THREE.DoubleSide,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor
    });
    this._discMat = mat(discFragment, 0, 0.5);
    this._pointMat = mat(pointFragment, 1, 1);
    this._discScene = new THREE.Scene();
    const discs = new THREE.Mesh(geo, this._discMat); discs.frustumCulled = false; this._discScene.add(discs);
    this._points = new THREE.Mesh(geo, this._pointMat); this._points.frustumCulled = false; this._points.renderOrder = -999;
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._compU = { uTex: { value: null } };
    this._comp = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._compU, vertexShader: quadVertex, fragmentShader: compositeFragment, depthTest: false, depthWrite: false }));
    this._comp.frustumCulled = false; this._comp.renderOrder = -1000;
    const self = this;
    this._comp.onBeforeRender = function (renderer) { self._passes(renderer); };
    scene.add(this._comp); scene.add(this._points);

    // the timeline
    this._time = 0; this._sinceBeat = 9; this._sinceBar = 0;
    this._sceneIdx = 0;                       // the scene the sources are at (or gliding to)
    this._phase = 'blur'; this._phaseT = 0; this._focus = 0; this._burst = 0;
    this._glideT = 1; this._glideDur = 1; this._g = 1;
    this._events = T_EVENTS.slice(); this._lastSharpEnd = -99; this._queued = false;
    this._waveT = 9; this._rts = null;
    this._coverGrid = null; this._coverKey = undefined; this._job = 0;
    this._title = ''; this._trackKey = undefined;
    // the first scene: the cover, already in place, in blur
    this._buildScene(this._forceScene || 'cover', true);
  }

  // --- scenes: N targets, sorted by screen x (the glides stay local) ---------------------------
  _layout() {
    const a = safeZoneUniforms.uAspect.value || 1.78;
    const hs = safeZoneUniforms.uStageHalfSize.value;
    const hx = Math.max(0.12, hs.x / 2);
    const side = Math.max(0.2, a / 2 - 0.05 - (hx + 0.05));
    return { a, hx, side, leftC: -(hx + 0.05 + side / 2), rightC: hx + 0.05 + side / 2 };
  }
  _targets(name) {
    const N = this._N, R = rng(1 + Math.floor(Math.random() * 1e6)), L = this._layout();
    const t = [];   // [x, y, z, flux, r, g, b, sharpR]
    const flat = (sx, sy, flux, col, sharp) => t.push([sx * Z_FLAT, sy * Z_FLAT, Z_FLAT, flux, col[0], col[1], col[2], sharp]);
    const world = (x, y, z, flux, col, sharp) => t.push([x, y, z, flux, col[0], col[1], col[2], sharp != null ? sharp : 0.0028]);
    const pick = (arr) => arr[Math.floor(R() * arr.length)];
    const world0 = world;
    if (name === 'cover') {
      const G = this._G, size = Math.min(0.62, L.side * 0.94, 0.86), sp = size / G;
      this._mosaic = { cx: L.leftC, size: sp * G };
      const img = this._coverGrid || fallbackGrid(null, G);
      for (let y = 0; y < G; y++) for (let x = 0; x < G; x++) {
        const o = (y * G + x) * 4, c = lin([img[o] / 255, img[o + 1] / 255, img[o + 2] / 255]);
        const m = Math.max(c[0], c[1], c[2], 1e-3);
        flat(L.leftC + (x - (G - 1) / 2) * sp, ((G - 1) / 2 - y) * sp, 0.06 + 0.94 * Math.pow(m, 0.8), [c[0] / m, c[1] / m, c[2] / m], sp * 0.36);
      }
    } else if (name === 'title') {
      const rows = this._L.rows;
      let d = null, sp = 0;
      for (let rpl = rows; rpl >= 6 && !d; rpl--) {
        const maxCols = 80, td = titleDots(this._title || 'Halofloor', rpl, 3, maxCols);
        if (!td) break;
        const s = Math.min(L.side * 0.94 / td.cols, 0.62 / td.rows);
        if (td.cells.length <= N * 0.9 && s >= 0.0075) { d = td; sp = s; }
      }
      if (d) {
        const col = this._titleCol || [1, 0.72, 0.4];
        for (const [x, y] of d.cells) flat(L.rightC + (x - (d.cols - 1) / 2) * sp, ((d.rows - 1) / 2 - y) * sp, 0.85, col, sp * 0.36);
      }
    } else if (name === 'avenue') {
      const n = N, HZ = -0.28;                           // the horizon just under the cover block
      // the distance takes light away: far lights pile up at the vanishing point and must not add up to white
      const world = (x, y, z, flux, col, sharp) => world0(x, y + HZ * z, z, flux / (1 + z / 30), col, sharp != null ? sharp : 0.003);
      for (let i = 0; i < n * 0.18; i++) {              // cars: headlights coming, tail lights going
        const z = 5 + Math.pow(R(), 1.6) * 45, away = R() < 0.5;
        const x = away ? 1.7 : -1.7, col = away ? [1, 0.12, 0.08] : [1, 0.9, 0.72];
        world(x - 0.42, -1.45, z, away ? 0.55 : 0.95, col); world(x + 0.42, -1.45, z, away ? 0.55 : 0.95, col); i++;
      }
      for (let z = 6; t.length < n * 0.34; z += 4.2) {    // street lamps, both sides
        world(-5.2, 2.8, z, 0.85, [1, 0.62, 0.24]); world(5.2, 2.8, z, 0.85, [1, 0.62, 0.24]);
      }
      while (t.length < n * 0.80) {                      // windows on the facades
        const side = R() < 0.5 ? -1 : 1;
        world(side * (8.5 + R() * 0.4), -0.6 + Math.floor(R() * 18) * 0.8, 7 + Math.floor(Math.pow(R(), 1.7) * 24) * 2, 0.32, R() < 0.72 ? WARM[1] : COOL[1]);
      }
      while (t.length < n) {                            // signs: short rows of pink and cyan
        const side = R() < 0.5 ? -1 : 1, z0 = 8 + R() * 30, y0 = 1 + R() * 3, col = R() < 0.55 ? [1, 0.3, 0.6] : [0.2, 0.85, 1];
        for (let k = 0; k < 8 && t.length < n; k++) world(side * 8.3, y0, z0 + k * 0.35, 0.6, col);
      }
    } else if (name === 'sky') {
      const n = N;
      while (t.length < n * 0.72) {                     // stars, far, in the upper two thirds
        const sx = (R() - 0.5) * L.a * 0.96, sy = 0.48 - Math.pow(R(), 1.6) * 0.72, z = 90 + R() * 40;
        world(sx * z, sy * z, z, 0.18 + 0.4 * R() * R(), R() < 0.7 ? [1, 0.95, 0.88] : COOL[0], 0.0016);
      }
      while (t.length < n) {                            // paper lanterns drifting at mid depth
        const z = 16 + R() * 24, sx = (R() - 0.5) * L.a * 0.9, sy = -0.3 + R() * 0.75;
        world(sx * z, sy * z, z, 0.7 + 0.3 * R(), pick([[1, 0.55, 0.18], [1, 0.3, 0.16], [1, 0.42, 0.55]]), 0.0045 + 0.004 * R());
      }
    }
    // the sources a scene leaves unused: a dim far city, still in the frame
    // (pale, out of focus in front for the mosaic and the title, so the sharp scene reads alone)
    const flatScene = name === 'cover' || name === 'title';
    while (t.length < N) {
      const z = flatScene ? 3 + R() * 2.5 : 30 + R() * 30, sx = (R() - 0.5) * L.a * 0.95, sy = (R() - 0.5) * 0.9;
      world(sx * z, sy * z, z, 0.3 + 0.3 * R(), pick([WARM[0], WARM[1], COOL[1]]), -0.0018);
    }
    for (const p of t) { const c = lin([p[4], p[5], p[6]]); const m = Math.max(...c, 1e-3); p[4] = c[0] / m; p[5] = c[1] / m; p[6] = c[2] / m; }
    t.length = N;
    t.sort((p, q) => p[0] / p[2] - q[0] / q[2]);
    return t;
  }

  // start a glide to scene `name` (from where the sources are now)
  _buildScene(name, instant) {
    const A = this._arr, N = this._N, g = this._g;
    // from = the current positions (a glide may be interrupted)
    for (let i = 0; i < N * 4; i++) { A.from[i] = A.from[i] + (A.to[i] - A.from[i]) * g; A.cf[i] = A.cf[i] + (A.ct[i] - A.cf[i]) * g; }
    const T = this._targets(name);
    for (let i = 0; i < N; i++) {
      const p = T[i];
      A.to[i * 4] = p[0]; A.to[i * 4 + 1] = p[1]; A.to[i * 4 + 2] = p[2]; A.to[i * 4 + 3] = p[3];
      A.ct[i * 4] = p[4]; A.ct[i * 4 + 1] = p[5]; A.ct[i * 4 + 2] = p[6]; A.ct[i * 4 + 3] = p[7];
    }
    if (instant) { A.from.set(A.to); A.cf.set(A.ct); }
    for (const k in this._attr) this._attr[k].needsUpdate = true;
    this._sceneName = name;
    this._g = instant ? 1 : 0; this._glideT = 0;
  }

  // the cover's colours, asked to the worker at a track change
  _requestCover(profile) {
    const url = profile.artworkUrl || null;
    const pal = profile.artworkPalette;
    const key = (url || '') + '|' + (pal ? String((pal.vividColors || []).slice(0, 2)) : '');
    if (key === this._coverKey) return;
    this._coverKey = key;
    const G = this._G, id = ++_nextId, job = ++this._job;
    const pv = pal && (pal.vividColors || pal.dominantColors);
    if (pv && pv.length) { const c = vdjColor(pv[0], new THREE.Color()).convertLinearToSRGB(); this._titleCol = [c.r, c.g, c.b]; }
    const done = (data) => {
      this._coverGrid = data;
      // the sources already on (or going to) the cover take its colours at once
      if (this._sceneName === 'cover') this._recolourCover();
    };
    if (!url) { this._coverLog = 'no url'; done(fallbackGrid(pal, G)); return; }
    // absolute: a worker made from a blob URL cannot resolve a relative one
    let abs = artProxyUrl(url);
    try { abs = new URL(abs, typeof location !== 'undefined' ? location.href : undefined).href; } catch (e) { /* keep it */ }
    const sent = askWorker({ id, url: abs, g: G }, (m) => {
      if (job !== this._job || !this._scene) return;
      this._coverLog = m.error ? 'error ' + m.error : 'ok ' + Math.round(m.ms) + ' ms';
      done(m.error || !m.data ? fallbackGrid(pal, G) : new Uint8Array(m.data));
    });
    if (!sent) { this._coverLog = 'no worker'; done(fallbackGrid(pal, G)); }
  }
  _recolourCover() {
    // the mosaic's positions do not change, only colours and fluxes: rebuild the
    // targets and keep the glide where it is
    const T = this._targets('cover'), A = this._arr, N = this._N;
    for (let i = 0; i < N; i++) {
      const p = T[i];
      A.to[i * 4 + 3] = p[3];
      A.ct[i * 4] = p[4]; A.ct[i * 4 + 1] = p[5]; A.ct[i * 4 + 2] = p[6];
      if (this._g >= 1) { A.from[i * 4 + 3] = p[3]; A.cf[i * 4] = p[4]; A.cf[i * 4 + 1] = p[5]; A.cf[i * 4 + 2] = p[6]; }
    }
    for (const k in this._attr) this._attr[k].needsUpdate = true;
  }

  _nextSceneName() {
    if (this._forceScene) return this._forceScene;
    this._sceneIdx = (this._sceneIdx + 1) % SCENES.length;
    return SCENES[this._sceneIdx];
  }

  _bpm(profile) { return (profile.trackFeatures && profile.trackFeatures.bpm) || profile.bpm || null; }

  update(deltaTime, profile) {
    if (profile.playbackState !== 'PLAYING') return;          // pause: frozen, never black
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    const t = this._time;
    const a = this._adapt.update(dt, profile);
    this._u.uTime.value = t;
    this._requestCover(profile);
    const key = profile.trackId || profile.title;
    if (key !== this._trackKey) { this._trackKey = key; this._title = shortTitle(profile.title || ''); }

    // the beat: a breath of focus; the bar: a wave of sparkle (every 4 s without a grid)
    if (a.beat) this._sinceBeat = 0; else this._sinceBeat += dt;
    this._sinceBar += dt;
    if (a.bar || this._sinceBar > 6) { this._sinceBar = 0; this._waveT = 0; }
    this._waveT += dt;
    const L = this._layout();
    const wd = 1.4;
    this._u.uWave.value.set(-L.a / 2 - 0.2 + (L.a + 0.4) * clamp01(this._waveT / wd), this._waveT < wd ? 0.45 : 0, 0.12);

    // focus events: 4 s, 14 s, then the phrases (or every EVENT_GAP s)
    if (a.drop) { this._phase = 'burst'; this._phaseT = 0; this._dropFrom = this._focus; }
    let want = false;
    if (this._events.length && t >= this._events[0] - T_IN) { this._events.shift(); want = true; }
    if (!this._events.length && (a.phrase || t - this._lastSharpEnd > EVENT_GAP)) want = true;
    if (want) this._queued = true;
    const bpm = this._bpm(profile);
    // two bars; while the 4 s / 14 s openings remain, short enough to keep the second one
    const hold = Math.min(this._events.length ? 5 : 7, Math.max(3.5, bpm ? 8 * 60 / bpm : 4.8));
    this._phaseT += dt;
    switch (this._phase) {
      case 'blur':
        this._focus = 0;
        if (this._queued && this._g >= 1) { this._queued = false; this._phase = 'in'; this._phaseT = 0; }
        break;
      case 'in':
        this._focus = smoother(this._phaseT / T_IN);
        if (this._phaseT >= T_IN) { this._phase = 'hold'; this._phaseT = 0; }
        break;
      case 'hold':
        this._focus = 1;
        if (this._phaseT >= hold) { this._phase = 'out'; this._phaseT = 0; }
        break;
      case 'out':
        this._focus = 1 - smoother(this._phaseT / T_OUT);
        if (this._phaseT >= T_OUT) {
          this._phase = 'blur'; this._phaseT = 0; this._lastSharpEnd = t; this._queued = false;
          // glide to the next scene while everything is soft
          const nextAt = this._events.length ? this._events[0] - T_IN : t + Math.max(3, EVENT_GAP - 3);
          this._glideDur = Math.max(1.6, Math.min(6, nextAt - t - 0.4));
          this._buildScene(this._nextSceneName(), false);
        }
        break;
      case 'burst': {
        // the drop: maximum blur in 0.35 s, then everything glides onto the cover
        const k = smoother(this._phaseT / 0.35);
        this._focus = this._dropFrom * (1 - k);
        this._burst = k;
        if (this._phaseT >= 0.35) {
          this._phase = 'blur'; this._phaseT = 0; this._queued = true; this._glideDur = 1.8;
          if (!this._forceScene) { this._sceneIdx = 0; this._buildScene('cover', false); }
        }
        break;
      }
    }
    if (this._phase !== 'burst') this._burst = Math.max(0, this._burst - dt / 2.5);
    // the glide (only while blurred)
    if (this._g < 1) { this._glideT += dt; this._g = smoother(this._glideT / this._glideDur); }

    let f = this._focus;
    if (this._forceFocus != null) f = this._forceFocus;
    const optic = SCENE_OPTICS[this._sceneName] || SCENE_OPTICS.cover;
    // the breath: the focus plane moves by 3 % on the beat (sharp only, where it shows)
    const br = (vdjBeatBreath(this._sinceBeat, BREATH) - 1) / BREATH.breath.gain;
    const invSharp = (1 / optic.z) * (1 + 0.03 * br);
    this._u.uInvF.value = INV_F_BLUR + (invSharp - INV_F_BLUR) * f;
    this._u.uAp.value = (AP_BLUR + (optic.ap * AP_BLUR - AP_BLUR) * f) * (1 + (AP_BURST / AP_BLUR - 1) * this._burst);
    this._u.uBlade.value = this._L.blades ? smoother((f - 0.25) / 0.6) : 0;
    this._u.uDrift.value = 1 - 0.85 * f;
    this._u.uConv.value = smoother(Math.min(1, f * 1.15));
    this._u.uG.value = this._g;
    this._u.uRMax.value = this._L.rMax;
    stageCenterNdc(this._u.uStageNdc.value);
  }

  _passes(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_bs); w = b.x; h = b.y; }
    this._u.uPx.value = 1 / Math.max(1, h);
    const hw = Math.max(2, Math.round(w / 2)), hh = Math.max(2, Math.round(h / 2));
    if (!this._rts || this._rts.width !== hw || this._rts.height !== hh) {
      if (this._rts) this._rts.dispose();
      this._rts = new THREE.WebGLRenderTarget(hw, hh, { depthBuffer: false, stencilBuffer: false, type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    }
    const cc = renderer.getClearColor(new THREE.Color()), ca = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(this._rts); renderer.clear(true, false, false); renderer.render(this._discScene, this._ortho);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    this._compU.uTex.value = this._rts.texture;
  }

  get probe() {
    return { scene: this._sceneName, phase: this._phase, focus: +this._focus.toFixed(3), glide: +this._g.toFixed(3), invF: +this._u.uInvF.value.toFixed(4), ap: +this._u.uAp.value.toFixed(4), burst: +this._burst.toFixed(2), title: this._title, cover: !!this._coverGrid, n: this._N, mosaic: this._mosaic, coverLog: this._coverLog, wave: +this._u.uWave.value.y.toFixed(2) };
  }

  dispose() {
    if (this._scene) { this._scene.remove(this._comp); this._scene.remove(this._points); }
    if (this._comp) { this._comp.onBeforeRender = null; this._comp.geometry.dispose(); this._comp.material.dispose(); }
    if (this._discMat) this._discMat.dispose();
    if (this._pointMat) this._pointMat.dispose();
    if (this._geo) this._geo.dispose();
    if (this._rts) this._rts.dispose();
    this._rts = null; this._scene = null; this._job++;
  }
}
